import React, { useState, useEffect } from "react";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import {
  buildUrl,
  TASK_STATUS_WITH_BBID,
  TASKSTATUS_ENDPOINT,
  BADGE_PHOTO_STATUS_ENDPOINT,
} from "../config/urls";
import { getCandidateData } from "../utility/candidate-data";
import { fetchWithTimeout } from "../helpers/fetcher";
import { getLocale } from "../utility/locale-helper";
import { Col } from "@amzn/stencil-react-components/layout";
import { DisplayNewUIComponent } from "../utility/new-ui-toggle";

/**
 * Current statuses for default tasks
 */
export enum StatusEnum {
  HIDDEN = "Hidden",
  LOADING = "Loading",
  NOT_STARTED = "Not Started",
  IN_PROGRESS = "In Progress",
  COMPLETED = "Completed",
  API_ERROR = "N/A",
  ERROR = "Error",
  PENDING_REVIEW = "Pending Review By Amazon",
  ELIGIBLE = "Eligible",
  INELIGIBLE = "Ineligible",
  INITIATED = "Initiated",
  NOT_REQUIRED = "Not Required",
  NOT_AVAILABLE_YET = "Not Available Yet",
  ACTION_REQUIRED = "Action Required",
}

const TaskStatusMap: Record<string, StatusEnum> = {
  ELIGIBLE: StatusEnum.ELIGIBLE,
  IN_PROGRESS: StatusEnum.IN_PROGRESS,
  SUBMITTED: StatusEnum.COMPLETED,
  COMPLETED: StatusEnum.COMPLETED,
  INELIGIBLE: StatusEnum.INELIGIBLE,
  NOT_AVAILABLE_YET: StatusEnum.NOT_AVAILABLE_YET,
  ACTION_REQUIRED: StatusEnum.ACTION_REQUIRED,
};

interface WithStatusProps {
  key: string;
  renderComponent: (status?: StatusEnum, taskId?: string) => JSX.Element;
  /**
   * Templated path i.e. 'api/resource1/123/resource2/456'
   */
  statusPath?: string;
  applicationId?: string;
  candidateId?: string;
  hideWhenError?: boolean;
  isFallback?: boolean;
  shouldOverrideStatus?: Record<string, StatusEnum>;
  taskStatusName?: string;
  hideOnTaskStatusError?: boolean;
  getTaskStatusByBBId?: boolean;
  bundle?: any;
  onTaskStatusEvent?: (k: string, v: StatusEnum) => void;
  hideOnConfig?: boolean | undefined;
}

export let badgePhoto: string;
const getStatus = async ({ applicationId, statusPath }: any) => {
  if (
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_USE_MOCK_REQUESTS === "true"
  ) {
    if (statusPath == BADGE_PHOTO_STATUS_ENDPOINT) {
      return StatusEnum.COMPLETED;
    }
    return StatusEnum.IN_PROGRESS;
  }
  try {
    const { candidateId, sfCandidateId } = await getCandidateData(
      applicationId
    );

    const locale = getLocale();

    const url = buildUrl(statusPath, {
      candidateId,
      sfCandidateId,
      applicationId,
      locale,
    });
    const { status, badgePhotoSrc } = await fetchWithTimeout(url);
    if (badgePhotoSrc) {
      badgePhoto = badgePhotoSrc;
    }
    return status;
  } catch (e) {
    if (e instanceof Error)
      console.error(
        ` Failed to retrieve status with path: ${statusPath} . Message: ${e.message}`
      );
    return StatusEnum.API_ERROR;
  }
};

let taskId: string;
const getTaskStatus = async ({
  applicationId,
  taskStatusName,
  getTaskStatusByBBId,
}: any) => {
  try {
    const { candidateId, sfCandidateId } = await getCandidateData(
      applicationId
    );

    if (process.env.NODE_ENV === "development") {
      if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
        console.warn(
          taskStatusName,
          "mocked request:",
          candidateId,
          sfCandidateId
        );
        taskId = "b0MbG14arf8xZKgR";
        return StatusEnum.ELIGIBLE;
      }
    }

    let url;
    if (getTaskStatusByBBId) {
      url = buildUrl(TASK_STATUS_WITH_BBID, {
        candidateId,
      });
    } else {
      url = buildUrl(TASKSTATUS_ENDPOINT, {
        candidateId,
        sfCandidateId,
        applicationId,
      });
    }
    const taskStatus = await fetchWithTimeout(url);

    if (taskStatus.errorCode) {
      if (taskStatus.errorCode != 404) {
        throw Error(
          `Error when requesting task status from ${url}: ${taskStatus.errorCode}, ${taskStatus.errorMessage}`
        );
      }
      return StatusEnum.API_ERROR;
    }
    const taskStatusObject = taskStatus?.find(
      (obj: { taskType: string }) => obj.taskType === taskStatusName
    );
    if (!taskStatusObject?.taskStatus) {
      if (taskStatusName === "NEW_HIRE_VIDEO") {
        //New hire video won't be in the database until the task is complete.
        return StatusEnum.NOT_STARTED;
      }
      return StatusEnum.API_ERROR;
    }

    if (getTaskStatusByBBId) {
      taskId = taskStatusObject.taskId;
    }
    return TaskStatusMap[taskStatusObject.taskStatus];
  } catch (e) {
    if (e instanceof Error)
      console.error(
        `Failed to retrieve status for task: ${taskStatusName} . Message: ${e.message}`
      );
    return StatusEnum.API_ERROR;
  }
};

export const withStatus = ({
  key,
  statusPath,
  applicationId,
  hideWhenError,
  isFallback,
  renderComponent,
  shouldOverrideStatus,
  taskStatusName,
  hideOnTaskStatusError,
  getTaskStatusByBBId,
  bundle,
  onTaskStatusEvent,
  hideOnConfig,
}: WithStatusProps) => {
  const [status, setStatus] = useState<StatusEnum>(StatusEnum.LOADING);
  const loadingText = bundle.getMessage("Checklist-General-LoadingTask");
  useEffect(() => {
    if (!statusPath && !taskStatusName) {
      return;
    }

    if (!hideOnConfig) {
      statusPath &&
        getStatus({ applicationId, statusPath }).then(function (status) {
          if (
            !DisplayNewUIComponent &&
            shouldOverrideStatus &&
            shouldOverrideStatus[status]
          ) {
            //for new UI, we don't override status here. Instead we do it in src/components/task/ui-redesign/index.tsx
            setStatus(shouldOverrideStatus[status]);
          } else {
            setStatus(status);
          }
        });
      taskStatusName &&
        getTaskStatus({
          applicationId,
          taskStatusName,
          getTaskStatusByBBId,
        }).then(function (status) {
          if (
            !DisplayNewUIComponent &&
            shouldOverrideStatus &&
            shouldOverrideStatus[status]
          ) {
            //for new UI, we don't override status here. Instead we do it in src/components/task/ui-redesign/index.tsx
            setStatus(shouldOverrideStatus[status]);
          } else {
            setStatus(status);
          }
        });
    }
  }, [statusPath, hideOnConfig]);

  useEffect(() => {
    // Do not count task in counter if task is loading
    if (status == StatusEnum.LOADING) {
      return;
    }

    if (
      onTaskStatusEvent &&
      !hideTask(
        key,
        status,
        isFallback,
        hideWhenError,
        hideOnTaskStatusError
      ) &&
      !hideOnConfig
    ) {
      //for new UI, override the status saved in status map to get correct amount of completed tasks
      onTaskStatusEvent(
        key,
        shouldOverrideStatus && shouldOverrideStatus[status]
          ? shouldOverrideStatus[status]
          : status
      );
    }
  }, [status, hideOnConfig]);

  if (!statusPath && !taskStatusName) {
    return renderComponent(undefined);
  }

  const spinner = <Spinner key={key} loadingText={loadingText} showText />;
  if (status === StatusEnum.LOADING && !hideOnConfig) {
    if (DisplayNewUIComponent) {
      return (
        <Col justifyContent={"center"} padding={"1rem"}>
          {spinner}
        </Col>
      );
    }
    return spinner;
  }

  if (
    hideTask(key, status, isFallback, hideWhenError, hideOnTaskStatusError) ||
    hideOnConfig
  ) {
    return null;
  }

  if (getTaskStatusByBBId) {
    return renderComponent(status, taskId);
  }
  return renderComponent(status);
};

export const hideTask = (
  key: string,
  status: StatusEnum,
  isFallback: boolean | undefined,
  hideWhenError: boolean | undefined,
  hideOnTaskStatusError: boolean | undefined
): boolean => {
  // Hide task if the status is Hidden or enableTask is false
  if (!isFallback && status === StatusEnum.HIDDEN) {
    return true;
  }

  // Hide fallback if the status is not Hidden and not Error
  if (
    isFallback &&
    status !== StatusEnum.HIDDEN &&
    status !== StatusEnum.API_ERROR
  ) {
    return true;
  }

  if (hideWhenError && status === StatusEnum.API_ERROR) {
    if (process.env.NODE_ENV === "production" || hideOnTaskStatusError) {
      return true;
    }
    console.warn(
      `The task "${key}" has "hideWhenError" set to true, that means it will be hidden in prod`
    );
  }

  return false;
};
